import { useId, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useListTenantCollectionTicketClaims } from '@/api/trafikportalen';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { Spinner } from '../core/Spinner';
import { FreePeriodBadge } from './FreePeriodBadge';
import { PeriodStatusBadge } from './PeriodStatusBadge';
import type { CollectionTypeOptInDTO } from '@/api/trafikportalen/models/collectionTypeOptInDTO';

interface StudentCategoryPeriodListProps {
  organizationId: string;
  collectionType: CollectionTypeOptInDTO;
}

export const StudentCategoryPeriodList = ({
  organizationId,
  collectionType,
}: StudentCategoryPeriodListProps) => {
  const headingId = useId();
  const claimParams = useMemo(
    () => ({
      collection_type: [collectionType],
      organization_ids: [organizationId],
    }),
    [collectionType, organizationId],
  );
  const {
    data: claims,
    isLoading,
    isError,
  } = useListTenantCollectionTicketClaims(claimParams);

  const visibleClaims = useMemo(() => {
    const current = (claims ?? []).flatMap((claim) => {
      const status = getPeriodStatus(claim);
      if (status === 'past') return [];
      return [{ claim, status }];
    });
    current.sort((a, b) =>
      (a.claim.valid_from ?? '').localeCompare(b.claim.valid_from ?? ''),
    );
    return current;
  }, [claims]);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h3 id={headingId}>
        <FormattedMessage defaultMessage="Aktuella och kommande perioder" />
      </h3>
      {isLoading ? (
        <div className="flex h-16 items-center justify-center">
          <Spinner className="size-4" />
        </div>
      ) : isError ? (
        <p role="alert" className="text-red-800">
          <FormattedMessage defaultMessage="Kunde inte hämta perioderna." />
        </p>
      ) : visibleClaims.length === 0 ? (
        <p className="italic text-text-500">
          <FormattedMessage defaultMessage="Inga aktuella eller kommande perioder." />
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {visibleClaims.map(({ claim, status }) => (
            <li
              key={claim.id}
              className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-background-300 px-4 py-3"
            >
              <PeriodDateRange
                from={claim.valid_from}
                to={claim.valid_to}
                className="font-medium"
              />
              <PeriodStatusBadge status={status} />
              {claim.non_invoiced && <FreePeriodBadge />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
